import React, {useCallback, useContext, useState} from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import {styled} from '@mui/material/styles';
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import {Fab} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import DragHandleIcon from '@mui/icons-material/DragHandle';
import {v4 as uuidv4} from 'uuid';
import {UserContext} from "../../context/userContext";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import {AuthContext} from "../../context/authContext";
import frugalServicesClient from "../../utils/frugal-services-client";


const PREFIX = 'PriorityListInput';
const classes = {
  root: `${PREFIX}-root`,
  paper: `${PREFIX}-paper`,
  rightIcon: `${PREFIX}-rightIcon`,
}
const Root = styled('form')(({ theme }) => ({
  [`&.${classes.root}`]: {
    flexGrow: 1,
  },
  [`& .${classes.paper}`]: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    width: '100%'
  },
  [`& .${classes.rightIcon}`]: {
    marginLeft: theme.spacing(1),
  },
  [`& .${classes.titleContainer}`]: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  [`& .${classes.total}`]: {
    marginLeft: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}))

export default function PriorityListInput(props) {
  const open = props.open;
  const {setPasswordExpired} = useContext(AuthContext);
  const {user} = useContext(UserContext);
  const[items, setItems] = useState([]);
  const[title, setTitle] = useState('');
  const [itemDescription, setItemDescription] = useState('');
  const [itemAmount,setItemAmount] = useState(0);
  const handleClose = props.closeHandler;

  // Change handlers
  const handleTitleChange = e => setTitle(e.target.value);
  const handleAddItemDescriptionChange = (e,v)=>  setItemDescription(e.target.value);
  const handleAddItemAmountChange = (e,v) => setItemAmount(e.target.value);
  const handleAddItem = (e,v) => {
    if(itemDescription !== "") {
      const newItem = {"id": uuidv4(), "rank": items.length, "description": itemDescription, "price": itemAmount};
      setItems([...items, newItem]);
      // clear fields
      setItemDescription("");
      setItemAmount(0);
    } else {
      // todo: display toast
    }
  }

  const handleRemoveItem = useCallback((itemId) => {
    setItems(prevItems => prevItems.filter(item => item.id !== itemId));
  }, []);

  const onDragEnd = useCallback((result) => {
    if (!result.destination) {
      return; // Dropped outside the list
    }

    // First sort items based on dragged  order
    const newItems = Array.from(items);
    const [reorderedItem] = newItems.splice(result.source.index, 1);
    newItems.splice(result.destination.index, 0, reorderedItem);

    // Update rank field for all items
    const updatedItems = newItems.map((item, index) => ({
      ...item,
      rank: index,
    }));

    setItems(updatedItems);
  }, [items]);

  // Calculate list total
  const calculateTotal = useCallback(() => { //Calculate total
    return items.reduce((sum, item) => sum + parseFloat(item.price || 0), 0);
  }, [items]);


  return (
     <Root>
       <Dialog open={open} onClose={handleClose} maxWidth={'md'}>
         <DialogTitle>
           <div className={classes.titleContainer}>
             <span>Create Priority List</span>
             <Typography variant="subtitle1" className={classes.total}>
               Total: {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(calculateTotal())}
             </Typography>
           </div>
         </DialogTitle>
         <DialogContent>
           {/*Title Input*/}
           <Grid container>
             <Grid size={6}>
               <TextField
                  id="list-item"
                  label="List title"
                  fullWidth
                  variant="standard"
                  value={title}
                  onChange={handleTitleChange}
               />
             </Grid>
           </Grid>
           {/*New Input*/}
           <Grid container sx={{ paddingTop: "5px" }} rowSpacing={2} columnSpacing={2} alignItems={"center"}>
             <Grid size={6}>
               <TextField
                  id="list-item"
                  label="Description"
                  fullWidth
                  variant="standard"
                  value={itemDescription}
                  onChange={handleAddItemDescriptionChange}
               />
             </Grid>
             <Grid xs={4}>
               <TextField
                  id="list-item-amount"
                  label="Amount"
                  type='number'
                  variant="standard"
                  inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
                  value={itemAmount === 0 ? "" : itemAmount}
                  onChange={handleAddItemAmountChange}
               />
             </Grid>
             <Grid xs={2}>
               <Fab color="primary" aria-label="add" onClick={handleAddItem}>
                 <AddIcon />
               </Fab>
             </Grid>
           </Grid>

           {/*Display list*/}
           <DragDropContext onDragEnd={onDragEnd}>
             <Droppable droppableId="droppable-list">
               {(provided) => (
                  <List sx={{
                    maxHeight: '600px',
                    overflowY: 'auto',
                    width: '100%',
                    scrollbarWidth: 'thin',
                  }} ref={provided.innerRef} {...provided.droppableProps}>
                    {items.map((item, index) => (
                       <Draggable key={item.id} draggableId={item.id} index={index}>
                         {(provided) => (
                            <ListItem
                               ref={provided.innerRef}
                               {...provided.draggableProps}
                               {...provided.dragHandleProps}
                               sx={{
                                 mb: 1, // Margin between items
                                 '&:hover': {
                                   backgroundColor: 'action.hover',
                                 },
                                 userSelect: 'none',
                                 display: 'flex', // Use flexbox for layout
                                 alignItems: 'center', // Vertically center items
                               }}
                            >
                              <ListItemIcon>
                                <DragHandleIcon />
                              </ListItemIcon>
                              <ListItemText
                                 primary={item.description}
                                 secondary={`$${item.price}`}  // Display price
                                 sx={{ flexGrow: 1 }} // Allow text to take up remaining space
                              />
                              <IconButton
                                 edge="end"
                                 aria-label="delete"
                                 onClick={() => handleRemoveItem(item.id)} // Pass item.id
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ListItem>
                         )}
                       </Draggable>
                    ))}
                    {provided.placeholder}
                  </List>
               )}
             </Droppable>
           </DragDropContext>

         </DialogContent>
         <DialogActions>
           <Button onClick={props.closeHandler}>Cancel</Button>
           <Button color='secondary' onClick={saveList}>Save</Button> {/*call save list function*/}
         </DialogActions>
       </Dialog>
     </Root>
  );


  function saveList() {
    frugalServicesClient.createPriorityList({
      title: title,
      items: items,
      accountId: user.accountId
    }).then(function (response) {
      // Exit and require login
      if(response.status === 401) {
        setPasswordExpired(true);
      }
      handleClose();
    }).catch(function (error) {
        console.error('Request failure: ', error);
    });
  }
}
